/**
 * 브랜드 상세 개요의 **파는 쪽 요약** — 「광고로 무엇을 말하나」 · 「어디서 얼마에 파나」의 숫자와 순위.
 * 🔑 상세 개요와 홈 질문 답(브랜드 개요 · `ask-answer-hubs`)이 같은 함수를 쓴다 — 두 자리의 수가 같다.
 * ⚠️ 'use client' 가 없다 — 서버에서도 부른다.
 */

import type { AdStatsResponse } from 'src/types/ads-meta/ads-meta-ops';
import type { ProductRankingSearchItem } from 'src/types/product-rankings/product-rankings-domain';
import type { ProductRankingSearchResponse } from 'src/types/product-rankings/product-rankings-ops';

import { fNumber } from 'src/utils/format/format-number';

import { PRODUCT_SOURCE_LABELS } from 'src/constants/product-sources';
import { pickAdLabels, AD_OFFER_LABELS } from 'src/constants/ad-labels';
import { findProductPriceBandSpec, formatProductPriceBandLabel } from 'src/constants/product-price-bands';

type Row = { name: string; count: number };

/**
 * 광고 요약 — 서버가 전량을 센 `ads.stats` 로(받아 둔 표본으로 세지 않는다).
 * @returns 0건이면 `null`
 */
export function toBrandHubAdStats(raw: AdStatsResponse | null | undefined) {
  if (!raw || raw.total === 0) return null;
  return {
    total: raw.total,
    freshCount: raw.freshCount,
    avgLifespanDays: raw.avgLifespanDays,
    facts: [
      { label: '집행 중 소재', value: fNumber(raw.total) },
      { label: '최근 7일 새 소재', value: fNumber(raw.freshCount) },
      { label: '평균 집행', value: raw.avgLifespanDays > 0 ? `${fNumber(raw.avgLifespanDays)}일` : '—' },
    ],
    offers: raw.offers.map((row): Row => ({ name: AD_OFFER_LABEL[row.name] ?? row.name, count: row.count })),
    // ⚠️ 코드 그대로 싣는다 — 이름표(국기 + 코드 · 「미상」)는 `HubRankingList format="country"` 가 붙인다
    countries: raw.countries.map((row): Row => ({ name: row.name, count: row.count })),
  };
}

/**
 * 상품 요약 — 패싯은 상품 검색 응답에 실려 온다(새 조회 없음).
 * ⚠️ 평균 할인율만은 **받아 둔 표본**으로 센다(패싯에 없다).
 * @returns 패싯이 없거나 0건이면 `null`
 */
export function toBrandHubProductStats(facets: ProductRankingSearchResponse['facets'] | null | undefined, total: number, items: readonly ProductRankingSearchItem[]) {
  if (!facets || total === 0) return null;
  const discounts = items.map((item) => item.discountRate).filter((v): v is number => typeof v === 'number' && v > 0);
  const avgDiscount = discounts.length > 0 ? Math.round(discounts.reduce((sum, v) => sum + v, 0) / discounts.length) : 0;
  const mallCount = Object.values(facets.sources ?? {}).filter((count) => count > 0).length;
  return {
    avgDiscount,
    mallCount,
    facts: [
      { label: '평균 할인율', value: avgDiscount > 0 ? `${avgDiscount}%` : '—' },
      { label: '판매몰', value: `${fNumber(mallCount)}곳` },
      { label: '상세 수집됨', value: fNumber(facets.withDetail ?? 0) },
    ],
    malls: toRows(facets.sources, (key) => PRODUCT_SOURCE_LABELS[key] ?? key),
    prices: toRows(facets.prices, (key) => {
      const spec = findProductPriceBandSpec(key);
      return spec ? formatProductPriceBandLabel(spec) : key;
    }),
  };
}

// ---- 이하 비공개 ----------------------------------------------------------

const AD_OFFER_LABEL = pickAdLabels(AD_OFFER_LABELS, 'short');

/** 패싯(이름 → 수) → 많은 순 줄 · 0 은 뺀다 */
function toRows(map: Record<string, number> | undefined, label: (key: string) => string): Row[] {
  return Object.entries(map ?? {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => ({ name: label(key), count }));
}
